import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useE2ee } from '../lib/e2ee-context';
import {
  importPublicKey,
  deriveSharedKey,
  encryptMessage,
  decryptMessage,
  generateGroupKey,
  wrapGroupKey,
  unwrapGroupKey,
  toBase64,
  fromBase64,
} from '../lib/e2ee';
import { MessageSquarePlus, Send, Users, Lock, ShieldCheck } from 'lucide-react';

interface ConvMember { userId: string; email: string; role: string }
interface ConvSummary {
  id: string;
  type: 'dm' | 'group';
  name: string | null;
  members: ConvMember[];
  wrappedKey: string | null;
  wrappedKeyIv: string | null;
  lastMessage: { createdAt: string; sender: { email: string } } | null;
  updatedAt: string;
}
interface EncryptedMsg {
  id: string;
  senderId: string;
  senderEmail: string;
  ciphertext: string;
  iv: string;
  createdAt: string;
}
interface DecryptedMsg {
  id: string;
  senderId: string;
  senderEmail: string;
  text: string;
  createdAt: string;
}
interface UserKey { userId: string; publicKeyJwk: JsonWebKey; user: { email: string } }

export function ChatPage() {
  const { me } = useAuth();
  const { privateKey, isReady } = useE2ee();
  const [conversations, setConversations] = useState<ConvSummary[]>([]);
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [messages, setMessages] = useState<DecryptedMsg[]>([]);
  const [input, setInput] = useState('');
  const [userKeys, setUserKeys] = useState<UserKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewChat, setShowNewChat] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const msgEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastMsgTime = useRef<string>('');

  const activeConv = conversations.find((c) => c.id === activeConvId);

  // Lade Konversationen und User-Keys
  useEffect(() => {
    if (!isReady) return;
    void (async () => {
      const [convRes, keyRes] = await Promise.all([
        api.get<{ conversations: ConvSummary[] }>('/chat/conversations'),
        api.get<{ keys: UserKey[] }>('/chat/keys/users'),
      ]);
      setConversations(convRes.conversations);
      setUserKeys(keyRes.keys);
      setLoading(false);
    })();
  }, [isReady]);

  // Entschlüsselungs-Helper
  const decryptMessages = useCallback(async (
    encrypted: EncryptedMsg[],
    conv: ConvSummary,
  ): Promise<DecryptedMsg[]> => {
    if (!privateKey) return [];
    const results: DecryptedMsg[] = [];

    for (const msg of encrypted) {
      try {
        let key: CryptoKey;

        if (conv.type === 'dm') {
          // DM: ECDH shared key mit dem anderen User
          const otherId = conv.members.find((m) => m.userId !== me?.id)?.userId;
          const otherKeyData = userKeys.find((k) => k.userId === otherId);
          if (!otherKeyData) { results.push({ ...msg, text: '[Key fehlt]' }); continue; }
          const otherPub = await importPublicKey(otherKeyData.publicKeyJwk);
          key = await deriveSharedKey(privateKey, otherPub);
        } else {
          // Gruppe: Wrapped group key entschlüsseln
          if (!conv.wrappedKey || !conv.wrappedKeyIv) {
            results.push({ ...msg, text: '[Gruppen-Key fehlt]' }); continue;
          }
          // Wir brauchen den Public Key des Conversation-Creators (admin)
          const creatorId = conv.members.find((m) => m.role === 'admin')?.userId;
          const creatorKeyData = userKeys.find((k) => k.userId === creatorId);
          if (!creatorKeyData) { results.push({ ...msg, text: '[Creator-Key fehlt]' }); continue; }
          const creatorPub = await importPublicKey(creatorKeyData.publicKeyJwk);
          key = await unwrapGroupKey(
            fromBase64(conv.wrappedKey),
            fromBase64(conv.wrappedKeyIv),
            privateKey,
            creatorPub,
          );
        }

        const text = await decryptMessage(fromBase64(msg.ciphertext), fromBase64(msg.iv), key);
        results.push({ ...msg, text });
      } catch {
        results.push({ ...msg, text: '[Entschlüsselung fehlgeschlagen]' });
      }
    }
    return results;
  }, [privateKey, me?.id, userKeys]);

  // Nachrichten laden wenn Konversation wechselt
  useEffect(() => {
    if (!activeConvId || !activeConv) return;
    setMessages([]);
    lastMsgTime.current = '';

    void (async () => {
      const res = await api.get<{ messages: EncryptedMsg[] }>(`/chat/conversations/${activeConvId}/messages`);
      const decrypted = await decryptMessages(res.messages, activeConv);
      setMessages(decrypted);
      if (res.messages.length > 0) {
        lastMsgTime.current = res.messages[res.messages.length - 1]!.createdAt;
      }
    })();

    // Polling starten
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      if (!lastMsgTime.current) return;
      try {
        const res = await api.get<{ messages: EncryptedMsg[] }>(
          `/chat/conversations/${activeConvId}/poll?after=${encodeURIComponent(lastMsgTime.current)}`,
        );
        if (res.messages.length > 0) {
          const decrypted = await decryptMessages(res.messages, activeConv);
          setMessages((prev) => [...prev, ...decrypted]);
          lastMsgTime.current = res.messages[res.messages.length - 1]!.createdAt;
        }
      } catch { /* ignore */ }
    }, 3000);

    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeConvId, activeConv, decryptMessages]);

  // Auto-Scroll
  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Nachricht senden
  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!input.trim() || !activeConv || !privateKey) return;

    try {
      let key: CryptoKey;

      if (activeConv.type === 'dm') {
        const otherId = activeConv.members.find((m) => m.userId !== me?.id)?.userId;
        const otherKeyData = userKeys.find((k) => k.userId === otherId);
        if (!otherKeyData) return;
        const otherPub = await importPublicKey(otherKeyData.publicKeyJwk);
        key = await deriveSharedKey(privateKey, otherPub);
      } else {
        if (!activeConv.wrappedKey || !activeConv.wrappedKeyIv) return;
        const creatorId = activeConv.members.find((m) => m.role === 'admin')?.userId;
        const creatorKeyData = userKeys.find((k) => k.userId === creatorId);
        if (!creatorKeyData) return;
        const creatorPub = await importPublicKey(creatorKeyData.publicKeyJwk);
        key = await unwrapGroupKey(
          fromBase64(activeConv.wrappedKey),
          fromBase64(activeConv.wrappedKeyIv),
          privateKey,
          creatorPub,
        );
      }

      const { ciphertext, iv } = await encryptMessage(input.trim(), key);

      const res = await api.post<{ messageId: string; createdAt: string }>(
        `/chat/conversations/${activeConv.id}/messages`,
        { ciphertext: toBase64(ciphertext), iv: toBase64(iv) },
      );

      setMessages((prev) => [...prev, {
        id: res.messageId,
        senderId: me!.id,
        senderEmail: me!.email,
        text: input.trim(),
        createdAt: res.createdAt,
      }]);
      lastMsgTime.current = res.createdAt;
      setInput('');
    } catch (err) {
      console.error('Send failed:', err);
    }
  }

  // Neue DM erstellen
  async function startDm(targetUserId: string) {
    if (!privateKey) return;
    try {
      const res = await api.post<{ conversationId: string; existing?: boolean }>('/chat/conversations', {
        type: 'dm',
        members: [{ userId: targetUserId }],
      });
      setActiveConvId(res.conversationId);
      setShowNewChat(false);
      // Konversationen neu laden
      const convRes = await api.get<{ conversations: ConvSummary[] }>('/chat/conversations');
      setConversations(convRes.conversations);
    } catch (err) {
      console.error('DM create failed:', err);
    }
  }

  // Neue Gruppe erstellen
  async function createGroup(e: React.FormEvent) {
    e.preventDefault();
    if (!privateKey || !groupName.trim() || selectedUserIds.size === 0) return;

    try {
      // Gruppen-Key generieren
      const groupKey = await generateGroupKey();

      // Für jeden Member den Gruppen-Key wrappen
      const memberData: Array<{
        userId: string;
        wrappedKey: string;
        wrappedKeyIv: string;
        role: string;
      }> = [];

      // Für mich selbst
      const myKeyData = userKeys.find((k) => k.userId === me?.id);
      if (myKeyData) {
        const myPub = await importPublicKey(myKeyData.publicKeyJwk);
        const { wrappedKey, iv } = await wrapGroupKey(groupKey, privateKey, myPub);
        memberData.push({
          userId: me!.id,
          wrappedKey: toBase64(wrappedKey),
          wrappedKeyIv: toBase64(iv),
          role: 'admin',
        });
      }

      // Für andere Members
      for (const uid of selectedUserIds) {
        const keyData = userKeys.find((k) => k.userId === uid);
        if (!keyData) continue;
        const pub = await importPublicKey(keyData.publicKeyJwk);
        const { wrappedKey, iv } = await wrapGroupKey(groupKey, privateKey, pub);
        memberData.push({
          userId: uid,
          wrappedKey: toBase64(wrappedKey),
          wrappedKeyIv: toBase64(iv),
          role: 'member',
        });
      }

      const res = await api.post<{ conversationId: string }>('/chat/conversations', {
        type: 'group',
        name: groupName.trim(),
        members: memberData,
      });

      setActiveConvId(res.conversationId);
      setShowNewGroup(false);
      setGroupName('');
      setSelectedUserIds(new Set());

      const convRes = await api.get<{ conversations: ConvSummary[] }>('/chat/conversations');
      setConversations(convRes.conversations);
    } catch (err) {
      console.error('Group create failed:', err);
    }
  }

  function toggleUserId(uid: string) {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  }

  function convDisplayName(c: ConvSummary): string {
    if (c.type === 'group') return c.name ?? 'Gruppe';
    const other = c.members.find((m) => m.userId !== me?.id);
    return other?.email ?? 'Chat';
  }

  if (!isReady) {
    return (
      <div className="flex items-center justify-center h-full text-slate-500">
        <Lock size={20} className="mr-2" /> E2E-Verschlüsselung wird initialisiert...
      </div>
    );
  }

  if (!privateKey) {
    return (
      <div className="flex items-center justify-center h-full text-red-500">
        E2E-Verschlüsselung konnte nicht initialisiert werden. Bitte neu einloggen.
      </div>
    );
  }

  const otherUsers = userKeys.filter((k) => k.userId !== me?.id);

  return (
    <div className="flex h-[calc(100vh-2rem)] -mt-2 gap-4">
      {/* Sidebar: Konversationen */}
      <div className="w-80 flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-emerald-600" />
            <span className="font-semibold text-sm text-slate-700">E2E Chat</span>
          </div>
          <div className="flex gap-1">
            <button onClick={() => setShowNewChat(true)} className="p-1.5 rounded hover:bg-slate-100" title="Neue DM">
              <MessageSquarePlus size={16} className="text-slate-500" />
            </button>
            <button onClick={() => { setShowNewGroup(true); setSelectedUserIds(new Set()); setGroupName(''); }} className="p-1.5 rounded hover:bg-slate-100" title="Neue Gruppe">
              <Users size={16} className="text-slate-500" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-sm text-slate-400">Lade...</div>
          ) : conversations.length === 0 ? (
            <div className="p-4 text-sm text-slate-400">Noch keine Chats. Starte einen neuen!</div>
          ) : (
            conversations.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveConvId(c.id)}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition ${
                  activeConvId === c.id ? 'bg-rla-50 border-l-2 border-l-rla-500' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  {c.type === 'group' ? <Users size={14} className="text-slate-400" /> : null}
                  <span className="font-medium text-sm text-slate-700 truncate">{convDisplayName(c)}</span>
                </div>
                {c.lastMessage && (
                  <p className="text-xs text-slate-400 mt-1 truncate">
                    {c.lastMessage.sender.email.split('@')[0]} · {new Date(c.lastMessage.createdAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                )}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat-Bereich */}
      <div className="flex-1 flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden">
        {!activeConv ? (
          <div className="flex-1 flex items-center justify-center text-slate-400">
            <div className="text-center">
              <Lock size={32} className="mx-auto mb-3 text-slate-300" />
              <p className="text-sm">Wähle einen Chat oder starte einen neuen</p>
              <p className="text-xs mt-1 text-slate-300">Alle Nachrichten sind Ende-zu-Ende verschlüsselt</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
              <Lock size={14} className="text-emerald-500" />
              <span className="font-semibold text-sm">{convDisplayName(activeConv)}</span>
              <span className="text-xs text-slate-400 ml-auto">
                {activeConv.members.length} Mitglieder · E2E verschlüsselt
              </span>
            </div>

            {/* Nachrichten */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((msg) => {
                const isMe = msg.senderId === me?.id;
                return (
                  <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] px-3 py-2 rounded-lg text-sm ${
                      isMe ? 'bg-rla-500 text-white' : 'bg-slate-100 text-slate-800'
                    }`}>
                      {!isMe && activeConv.type === 'group' && (
                        <p className="text-xs font-medium mb-1 opacity-70">{msg.senderEmail.split('@')[0]}</p>
                      )}
                      <p className="whitespace-pre-wrap break-words">{msg.text}</p>
                      <p className={`text-[10px] mt-1 ${isMe ? 'text-white/60' : 'text-slate-400'}`}>
                        {new Date(msg.createdAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={msgEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={(e) => void handleSend(e)} className="px-4 py-3 border-t border-slate-200 flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Nachricht schreiben..."
                className="input flex-1"
                autoFocus
              />
              <button type="submit" disabled={!input.trim()} className="btn btn-primary px-4">
                <Send size={16} />
              </button>
            </form>
          </>
        )}
      </div>

      {/* New DM Dialog */}
      {showNewChat && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setShowNewChat(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-slate-800 mb-3">Neue Direktnachricht</h3>
            {otherUsers.length === 0 ? (
              <p className="text-sm text-slate-500">Keine anderen User mit E2E-Keys gefunden.</p>
            ) : (
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {otherUsers.map((k) => (
                  <button
                    key={k.userId}
                    onClick={() => void startDm(k.userId)}
                    className="w-full text-left px-3 py-2 rounded hover:bg-slate-100 text-sm"
                  >
                    {k.user.email}
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => setShowNewChat(false)} className="btn w-full mt-3">Schließen</button>
          </div>
        </div>
      )}

      {/* New Group Dialog */}
      {showNewGroup && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setShowNewGroup(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={(e) => void createGroup(e)} className="space-y-4">
              <h3 className="font-semibold text-slate-800">Neuer Gruppenchat</h3>
              <div>
                <label className="label">Gruppenname</label>
                <input type="text" required value={groupName} onChange={(e) => setGroupName(e.target.value)}
                  className="input" placeholder="z.B. Projekt Alpha" />
              </div>
              <div>
                <label className="label">Mitglieder auswählen</label>
                <div className="space-y-1 max-h-48 overflow-y-auto border rounded-lg p-2">
                  {otherUsers.map((k) => (
                    <label key={k.userId} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-50 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.has(k.userId)}
                        onChange={() => toggleUserId(k.userId)}
                        className="rounded border-slate-300"
                      />
                      {k.user.email}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => setShowNewGroup(false)} className="btn">Abbrechen</button>
                <button type="submit" disabled={!groupName.trim() || selectedUserIds.size === 0} className="btn btn-primary">
                  Erstellen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
